import { css } from "@emotion/css";
import { text, space, themeTokens } from "@octopusdeploy/design-system-tokens";
import type React from "react";
import type { DesignSystemLinkHref, ShowLinkAsActive } from "../../routing";
import { resetStyles } from "../../utils/resetStyles";
import { Divider } from "../Divider";
import { AreaNavListLink } from "./AreaNavListLink";
export interface AreaNavListProps {
    listItems: ReadonlyArray<AreaNavListItem>;
    onLinkClicked?: (item: AreaNavListLinkItem) => void;
    hasTitle?: boolean;
}
export interface AreaNavListGroup {
    heading: string | undefined;
    items: ReadonlyArray<AreaNavListGroupItem>;
    hasTrailingDivider?: boolean;
}
export type AreaNavListGroupItem = AreaNavListLinkItem | AreaNavListLinkPlaceholder;
export type AreaNavListItem = Spacer | Divider | AreaNavListLinkItem | AreaNavListLinkPlaceholder | AreaNavListGroup;
type Spacer = "spacer";
type Divider = "divider";
export interface AreaNavListLinkItem {
    href: DesignSystemLinkHref;
    showLinkAsActive?: ShowLinkAsActive;
    label: string;
    accessibleName?: string;
    icon?: React.ReactNode;
    showNewBadge?: boolean;
    isVisible?: boolean;
}
interface AreaNavListLinkPlaceholder {
    placeholderLabel: string;
}
export function AreaNavList({ listItems, onLinkClicked }: AreaNavListProps) {
    const visibleListItems = listItems.filter(isAreaNavListItemVisible);
    return (<nav className={areaNavListStyles}>
            <ul className={areaNavListUlStyles}>
                {visibleListItems.map((item, itemIndex) => (<AreaNavListItem key={itemIndex} item={item} onLinkClicked={onLinkClicked}/>))}
            </ul>
        </nav>);
}
function AreaNavListItem({ item, onLinkClicked }: {
    item: AreaNavListItem;
    onLinkClicked?: (item: AreaNavListLinkItem) => void;
}) {
    if (isSpacer(item))
        return <AreaNavListSpacerListItem />;
    if (isDivider(item))
        return <AreaNavListDividerListItem />;
    if (isPlaceholderLabel(item))
        return <AreaNavListPlaceholderListItem item={item}/>;
    if (isGroup(item))
        return <AreaNavListGroupListItem group={item} onLinkClicked={onLinkClicked}/>;
    else
        return <AreaNavListLinkListItem item={item} onLinkClicked={onLinkClicked}/>;
}
function AreaNavListGroupListItem({ group, onLinkClicked }: {
    group: AreaNavListGroup;
    onLinkClicked?: (item: AreaNavListLinkItem) => void;
}) {
    return (<>
            <li>
                {group.heading && <div className={listGroupTitleStyles}>{group.heading}</div>}
                <ul className={areaNavListUlStyles} aria-label={group.heading}>
                    {group.items.filter(isAreaNavListItemVisible).map((linkItem: AreaNavListItem, index) => (<AreaNavListItem key={index} item={linkItem} onLinkClicked={onLinkClicked}/>))}
                </ul>
            </li>
            {group.hasTrailingDivider && <AreaNavListDividerListItem />}
        </>);
}
function AreaNavListLinkListItem({ item, onLinkClicked }: {
    item: AreaNavListLinkItem;
    onLinkClicked?: (item: AreaNavListLinkItem) => void;
}) {
    const onClick = () => {
        onLinkClicked?.(item);
    };
    return (<li>
            <div style={{ display: "flex", flexDirection: "row" }}>
                <AreaNavListLink label={item.label} href={item.href} showLinkAsActive={item.showLinkAsActive} accessibleName={item.accessibleName} onClick={onClick} icon={item.icon} showNewBadge={item.showNewBadge}/>
            </div>
        </li>);
}
function AreaNavListSpacerListItem() {
    return (<li>
            <div className={spacerStyles}></div>
        </li>);
}
function AreaNavListDividerListItem() {
    return (<li>
            <div className={dividerStyles}>
                <Divider />
            </div>
        </li>);
}
function AreaNavListPlaceholderListItem({ item }: {
    item: AreaNavListLinkPlaceholder;
}) {
    return <li className={placeholderItemStyles}>{item.placeholderLabel}</li>;
}
function isAreaNavListItemVisible(item: AreaNavListItem) {
    if (isLink(item))
        return item.isVisible !== false;
    if (isGroup(item)) {
        const visibleGroupItems = item.items.filter(isAreaNavListItemVisible);
        return visibleGroupItems.length > 0;
    }
    return true;
}
function isPlaceholderLabel(item: AreaNavListItem): item is AreaNavListLinkPlaceholder {
    return typeof item === "object" && "placeholderLabel" in item;
}
function isGroup(item: AreaNavListItem): item is AreaNavListGroup {
    return typeof item === "object" && "items" in item;
}
function isLink(item: AreaNavListItem): item is AreaNavListLinkItem {
    return typeof item === "object" && "href" in item;
}
function isSpacer(item: AreaNavListItem): item is Spacer {
    return item === "spacer";
}
function isDivider(item: AreaNavListItem): item is Divider {
    return item === "divider";
}
const areaNavListStyles = css({
    width: "13.5rem",
    height: "100%",
    padding: `${space[32]} ${space[16]} ${space[24]} ${space[16]}`,
    background: themeTokens.color.background.primary.transparent,
    overflowY: "auto",
    // to hide the scrollbar on the right side of the panel, while scrolling is enabled
    scrollbarWidth: "none",
    msOverflowStyle: "none",
    "&::-webkit-scrollbar": {
        width: 0,
        height: 0,
    },
});
const areaNavListUlStyles = css({
    ...resetStyles.list,
});
const listGroupTitleStyles = css({
    font: text.body.bold.xSmall,
    color: themeTokens.color.text.tertiary,
    textTransform: "uppercase",
    padding: `${space[4]} ${space[8]}`,
});
const spacerStyles = css({
    height: "1rem",
});
const dividerStyles = css({
    margin: `${space[24]} 0`,
});
const placeholderItemStyles = css({
    font: text.body.regular.medium,
    fontStyle: "italic",
    padding: `${space[4]} ${space[8]}`,
});
